import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { TokenStatusBadge } from './token-status-badge';
import { ConfirmationDialog } from './confirmation-dialog';
import { useAdminApiMutation } from '@/hooks/use-admin-api-query';
import { qk } from '@/lib/query/keys';

interface Channel {
  id: string;
  type: 'whatsapp' | 'instagram' | 'facebook';
  displayName: string;
  metaWabaId: string;
  metaBusinessId: string;
  whatsappWabaName: string;
  whatsappBusinessName: string;
  whatsappPhoneNumberId: string;
  whatsappDisplayPhoneNumber: string;
  maskedToken: string;
  tokenStatus: string;
  tokenExpiresAt: string | null;
  connectionType?: string | null;
  metaConnected: boolean;
  forwardingEnabled: boolean;
  createdAt: string;
  lastRefreshedAt: string | null;
  consecutiveFailures: number;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

type ConfirmAction = 'forwarding' | 'metaConnected' | 'refresh' | null;

export function ChannelDetailCard({ channel }: { channel: Channel }) {
  const queryClient = useQueryClient();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [localForwardingEnabled, setLocalForwardingEnabled] = useState(channel.forwardingEnabled);
  const [localMetaConnected, setLocalMetaConnected] = useState(channel.metaConnected);
  const [localTokenExpiresAt, setLocalTokenExpiresAt] = useState(channel.tokenExpiresAt);
  const [localLastRefreshedAt, setLocalLastRefreshedAt] = useState(channel.lastRefreshedAt);
  const [localConsecutiveFailures, setLocalConsecutiveFailures] = useState(channel.consecutiveFailures);

  // This card renders under three super-admin reads (channel detail, user
  // channels, workspace detail), and channel actions ripple into the lists
  // too. ponytail: invalidate the whole superAdmin scope — operator-only
  // data, only mounted queries refetch.
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: qk.superAdmin() });

  const patchForwarding = useAdminApiMutation<unknown, { enabled: boolean }>(
    (vars) => ({
      path: `/super-admin/channels/${channel.id}/forwarding`,
      init: { method: 'PATCH', body: JSON.stringify(vars) },
      fallback: `Failed to update ${channel.displayName}`,
    }),
    { onSuccess: invalidate },
  );
  const patchMetaConnected = useAdminApiMutation<unknown, { connected: boolean }>(
    (vars) => ({
      path: `/super-admin/channels/${channel.id}/meta-connected`,
      init: { method: 'PATCH', body: JSON.stringify(vars) },
      fallback: `Failed to update ${channel.displayName}`,
    }),
    { onSuccess: invalidate },
  );
  const refreshToken = useAdminApiMutation<{ success?: boolean; error?: string }>(
    () => ({
      path: `/super-admin/channels/${channel.id}/refresh-token`,
      init: { method: 'POST' },
      fallback: `Failed to update ${channel.displayName}`,
    }),
    { onSuccess: (data) => { if (data.success) invalidate(); } },
  );

  const actionLoading =
    patchForwarding.isPending || patchMetaConnected.isPending || refreshToken.isPending;

  function handleConfirm() {
    if (confirmAction === 'forwarding') {
      patchForwarding.mutate(
        { enabled: !localForwardingEnabled },
        {
          onSuccess: () => {
            setLocalForwardingEnabled(!localForwardingEnabled);
            toast.success(`Forwarding ${!localForwardingEnabled ? 'enabled' : 'disabled'} for ${channel.displayName}`);
          },
          onError: () => toast.error(`Failed to update ${channel.displayName}`),
          onSettled: () => setConfirmAction(null),
        },
      );
    } else if (confirmAction === 'metaConnected') {
      patchMetaConnected.mutate(
        { connected: !localMetaConnected },
        {
          onSuccess: () => {
            setLocalMetaConnected(!localMetaConnected);
            toast.success(`Meta connection updated for ${channel.displayName}`);
          },
          onError: () => toast.error(`Failed to update ${channel.displayName}`),
          onSettled: () => setConfirmAction(null),
        },
      );
    } else if (confirmAction === 'refresh') {
      refreshToken.mutate(undefined, {
        onSuccess: (data) => {
          if (data.success) {
            toast.success(`Token refreshed for ${channel.displayName}`);
            setLocalLastRefreshedAt(new Date().toISOString());
            setLocalConsecutiveFailures(0);
            const sixtyDaysFromNow = new Date();
            sixtyDaysFromNow.setDate(sixtyDaysFromNow.getDate() + 60);
            setLocalTokenExpiresAt(sixtyDaysFromNow.toISOString());
          } else {
            toast.error(`Token refresh failed: ${data.error || 'Unknown error'}`);
          }
        },
        onError: (err) =>
          toast.error(`Token refresh failed: ${err.userMessage ?? 'Unknown error'}`),
        onSettled: () => setConfirmAction(null),
      });
    }
  }

  function getDialogProps(): {
    title: string;
    description: string;
    confirmLabel: string;
    variant: 'default' | 'destructive';
  } {
    switch (confirmAction) {
      case 'forwarding':
        return localForwardingEnabled
          ? {
              title: 'Disable Messaging',
              description: `Sending and receiving will be stopped for ${channel.displayName} until re-enabled.`,
              confirmLabel: 'Disable Messaging',
              variant: 'destructive',
            }
          : {
              title: 'Enable Messaging',
              description: `Sending and receiving will resume for ${channel.displayName}.`,
              confirmLabel: 'Enable Messaging',
              variant: 'default',
            };
      case 'metaConnected':
        return localMetaConnected
          ? {
              title: 'Disconnect Meta',
              description: `This will mark ${channel.displayName} as disconnected from Meta. The channel will stop receiving webhooks and token refreshes.`,
              confirmLabel: 'Disconnect',
              variant: 'destructive',
            }
          : {
              title: 'Reconnect Meta',
              description: `This will mark ${channel.displayName} as connected to Meta. Token refreshes and webhook forwarding will resume.`,
              confirmLabel: 'Reconnect',
              variant: 'default',
            };
      case 'refresh':
        return {
          title: 'Force Token Refresh',
          description: `This will immediately refresh the access token for ${channel.displayName}. Use only if the token needs urgent renewal.`,
          confirmLabel: 'Refresh Token',
          variant: 'default',
        };
      default:
        return { title: '', description: '', confirmLabel: '', variant: 'default' };
    }
  }

  const dialogProps = getDialogProps();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Badge variant={channel.type === 'whatsapp' ? 'default' : 'secondary'}>
              {channel.type === 'instagram' ? 'Instagram' : channel.type === 'facebook' ? 'Facebook' : 'WhatsApp'}
            </Badge>
            <p className="text-base font-semibold">{channel.displayName}</p>
          </div>
          <p className="text-sm text-muted-foreground">{channel.whatsappBusinessName}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <TokenStatusBadge tokenExpiresAt={localTokenExpiresAt} connectionType={channel.connectionType} />
          {/* A Facebook Login token never expires and has no refresh exchange (AIT-621). */}
          {channel.connectionType !== 'facebook_login' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmAction('refresh')}
              disabled={actionLoading}
            >
              <RefreshCw className="size-3.5" />
              Force Refresh Token
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3">
          <span className="text-xs font-semibold text-muted-foreground">
            {channel.type === 'facebook' ? 'Page ID' : channel.type === 'instagram' ? 'Account ID' : 'WABA ID'}
          </span>
          <span className="text-sm">{channel.metaWabaId}</span>

          {channel.type === 'whatsapp' && (
            <>
              <span className="text-xs font-semibold text-muted-foreground">Phone</span>
              <span className="text-sm">{channel.whatsappDisplayPhoneNumber}</span>
            </>
          )}

          <span className="text-xs font-semibold text-muted-foreground">
            Access Token
          </span>
          <span className="font-mono text-sm">{channel.maskedToken}</span>

          <span className="text-xs font-semibold text-muted-foreground">Expires</span>
          <span className="text-sm">{formatDate(localTokenExpiresAt)}</span>

          <span className="text-xs font-semibold text-muted-foreground">Last Refreshed</span>
          <span className="text-sm">{localLastRefreshedAt ? formatDate(localLastRefreshedAt) : 'Never'}</span>

          {localConsecutiveFailures > 0 && (
            <>
              <span className="text-xs font-semibold text-muted-foreground">Refresh Failures</span>
              <span className={`text-sm ${localConsecutiveFailures >= 3 ? 'text-destructive' : ''}`}>
                {localConsecutiveFailures} consecutive
              </span>
            </>
          )}

          <span className="text-xs font-semibold text-muted-foreground">Connected</span>
          <span className="text-sm">{formatDate(channel.createdAt)}</span>
        </div>

        <Separator className="my-4" />

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Messaging</span>
            <Switch
              checked={localForwardingEnabled}
              onCheckedChange={() => setConfirmAction('forwarding')}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Meta Connected</span>
            <Switch
              checked={localMetaConnected}
              onCheckedChange={() => setConfirmAction('metaConnected')}
            />
          </div>
        </div>
      </CardContent>

      <ConfirmationDialog
        open={confirmAction !== null}
        onOpenChange={(open) => { if (!open) setConfirmAction(null); }}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmLabel={dialogProps.confirmLabel}
        onConfirm={handleConfirm}
        loading={actionLoading}
        variant={dialogProps.variant}
      />
    </Card>
  );
}
